import { useContext } from "react";

import { useLocation } from "react-router-dom";

import Box from "@/components/Box";
import Loading from "@/components/Loading";
import CardListContext from "@/contexts/CardListContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { useDataLoader } from "@/hooks/useDataLoader";
import useListControls from "@/hooks/useListControls";
import { PanelBoxV2 } from "@/tpo/Boxes";
import { NoItems } from "@/tpo/EmptySearch";
import InfiniteScroll, { InfiniteScrollContext } from "@/tpo/InfiniteScroll";
import SortMenu from "@/tpo/SortMenu";
import { Search } from "@/tpo/TextInput";
import { ViewerContext } from "@/tpo/Viewer";

import GeneDatapointSummary from "./GeneDatapointSummary";

const GENES_BIOMARKERS_SORTING_OPTIONS = [
  {
    label: "Gene Name: A - Z",
    value: "datapoint__name"
  },
  {
    label: "Gene Name: Z - A",
    value: "-datapoint__name"
  }
];

const PAGE_SIZE = 10;

export default function GenesBiomarkers({ userTest, query, skip }) {
  const viewerContext = useContext(ViewerContext);
  const location = useLocation();
  const testMode = new URLSearchParams(location.search).has("testMode");

  const controls = useListControls({
    sortOptions: GENES_BIOMARKERS_SORTING_OPTIONS
  });

  const orgContext = useOrganisationContext();
  const organisation = orgContext?.organisation;

  const { data, loading, fetchMore } = useDataLoader({
    query,
    variables: {
      userTestId: userTest.id,
      userId: viewerContext?.userId,
      first: PAGE_SIZE,
      after: "",
      search: controls.debouncedSearch,
      orderBy: GENES_BIOMARKERS_SORTING_OPTIONS.find(option => controls.sort === option.value)
        ?.value,
      includeModifiers: true,
      testMode: !testMode, // have it using test mode by default now,
      organisationId: organisation?.id
    },
    skip
  });

  const ctx = useContext(CardListContext);

  const items = data?.userDatapointConnections?.edges?.map(edge => edge.node);

  return (
    <>
      <PanelBoxV2
        maxWidth={[1020]}
        inner={{
          display: "flex",
          flexDirection: "column",
          gap: [20, 20, 40]
        }}
      >
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection={["column", "row", "row"]}
            gap={20}
            justifyContent={["center", "flex-end", "flex-end"]}
          >
            <Search
              value={controls.search}
              onChange={controls.setSearch}
              maxWidth={[null, 350]}
              minWidth={310}
              width="100%"
            />
            <Box display="flex" justifyContent="flex-end">
              <SortMenu
                value={controls.sort}
                setValue={controls.setSort}
                open={controls.sortMenuOpen}
                setOpen={controls.setSortMenuOpen}
                options={controls.sortOptions}
                placeholder="Sort By"
              />
            </Box>
          </Box>
        </Box>
        {!controls.search || items?.length ? (
          <InfiniteScroll
            loader={<Loading />}
            hasMore={data?.userDatapointConnections?.pageInfo?.hasNextPage}
            loading={loading}
            next={() => {
              const endCursor = data?.userDatapointConnections?.pageInfo?.endCursor;
              if (endCursor) {
                fetchMore({
                  variables: {
                    after: endCursor,
                    first: 10
                  }
                });
              }
            }}
            items={items}
          >
            <Box borderRadius={5}>
              <Box
                bg="haze"
                display="flex"
                flexDirection="column"
                gap={1}
                pb="1px"
                position="relative"
                minHeight={1000}
              >
                <InfiniteScrollContext.Consumer>
                  {({ itemsList, setBottomElement }) => (
                    <>
                      {itemsList.slice(0, -1).map(item => (
                        <GeneDatapointSummary
                          userDatapoint={item}
                          key={item.id}
                          py={[2, 2, 20]}
                          px={[20, 20, 40]}
                          bg="white"
                          openModal={() => {
                            ctx?.viewCard({
                              type: "datapoints",
                              item
                            });
                          }}
                        />
                      ))}
                      {itemsList.slice(-1).map(item => (
                        <GeneDatapointSummary
                          userDatapoint={item}
                          key={item.id}
                          py={[2, 2, 20]}
                          px={[20, 20, 40]}
                          bg="white"
                          openModal={() => {
                            ctx?.viewCard({
                              type: "datapoints",
                              item
                            });
                          }}
                          ref={setBottomElement}
                        />
                      ))}
                    </>
                  )}
                </InfiniteScrollContext.Consumer>
              </Box>
            </Box>
          </InfiniteScroll>
        ) : (
          <NoItems search={controls.search} loading={loading} />
        )}
      </PanelBoxV2>
    </>
  );
}
